import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";

import VendorHeader from "../component/VendorHeader";
import VendorFooter from "../component/VendorFooter";
import Sidebar from "../component/Sidebar";

import { API_BASE_URL } from "../utils/app_url";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { NotificationManager } from "react-notifications";

const VendorOrderListing = () => {
  const { user } = useAuth();
  const { langJson } = useLanguage();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]); // store orders
  const [photoOrders, setPhotoOrders] = useState([]); // photo orders
  const [loading, setLoading] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPhotoQuery, setSearchPhotoQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exporting, setExporting] = useState(false);

  /* =======================
     FETCH ORDERS
  ======================= */
  const fetchOrders = async () => {
    setLoading(true);
    setLoadingPhoto(true);
    try {
      const response = await fetch(`${API_BASE_URL}order/store-orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });

      const res = await response.json();

      if (res?.res === true && res.data) {
        setOrders(Array.isArray(res.data.orders) ? res.data.orders : []);
        setPhotoOrders(Array.isArray(res.data.photo_orders) ? res.data.photo_orders : []);
      } else {
        setOrders([]);
        setPhotoOrders([]);
      }
    } catch (err) {
      console.error("Fetch orders failed", err);
      setOrders([]);
      setPhotoOrders([]);
    } finally {
      setLoading(false);
      setLoadingPhoto(false);
    }
  };

  /* =======================
     STATUS CHANGE
  ======================= */
  const onStatusChange = async (status, id) => {
    if (!status || !id) return;

    // optimistic update
    setOrders(prev =>
      prev.map(o => (o.id === id ? { ...o, status } : o))
    );

    try {
      const response = await fetch(`${API_BASE_URL}order/update-order/${id}`, {
        method: "POST",
        body: JSON.stringify({ status }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });

      const res = await response.json();

      if (res?.res === true) {
        NotificationManager.success(`Status changed to ${status}`);
      } else {
        NotificationManager.error(res?.msg || "Status update failed");
      }
    } catch (err) {
      NotificationManager.error("Status update failed");
    }
  };

  /* =======================
     SEARCH & DATE FILTER
  ======================= */
  const isWithinDateRange = (dateStr) => {
    if (!fromDate && !toDate) return true;
    const orderDate = new Date(dateStr);
    if (fromDate && new Date(fromDate) > orderDate) return false;
    if (toDate && new Date(toDate) < orderDate) return false;
    return true;
  };

  const filteredOrders = orders.filter(item => {
    const matchesSearch = !searchQuery.trim() || (
      (item?.order_number && item.order_number.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item?.name && item.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item?.email && item.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item?.status && item.status.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item?.total_amount && String(item.total_amount).includes(searchQuery))
    );
    const matchesDate = isWithinDateRange(item?.ordered_date);
    return matchesSearch && matchesDate;
  });

  const filteredPhotoOrders = searchPhotoQuery.trim()
    ? photoOrders.filter(item => {
      const q = searchPhotoQuery.toLowerCase();
      return (
        (item?.id && String(item.id).toLowerCase().includes(q)) ||
        (item?.order_number && item.order_number.toString().toLowerCase().includes(q)) ||
        (item?.name && item.name.toLowerCase().includes(q)) ||
        (item?.email && item.email.toLowerCase().includes(q)) ||
        (item?.ordered_date && item.ordered_date.toLowerCase().includes(q))
      );
    })
    : photoOrders;

  /* =======================
     TABLE COLUMNS
  ======================= */
  const columns = [
    {
      name: langJson?.orderno || "Order No",
      selector: row => row.order_number,
      cell: row => (
        <span
          style={{ color: "#007bff", cursor: "pointer", fontWeight: 600 }}
          onClick={() => navigate(`/order/${row.id}`)}
        >
          {row.order_number}
        </span>
      ),
      sortable: true,
    },
    {
      name: langJson?.name || "Name",
      selector: row => row.name ?? "-",
      sortable: true,
    },
    {
      name: langJson?.country || "Country",
      selector: row => row.country ?? "-",
      sortable: true,
    },
    {
      name: langJson?.email || "Email",
      selector: row => row.email ?? "-",
      sortable: true,
    },
    {
      name: langJson?.quantity || "Qty",
      selector: row => row.quantity ?? 0,
      sortable: true,
    },
    {
      name: langJson?.price || "Price",
      selector: row =>
        `${row.currency ?? "₹"}${row.total_amount ?? 0}`,
      sortable: true,
    },
    {
      name: langJson?.orderdate || "Order Date",
      selector: row => row.ordered_date ?? "-",
      sortable: true,
    },
    {
      name: langJson?.status || "Status",
      cell: row => (
        <select
          value={row.status}
          onChange={e => onStatusChange(e.target.value, row.id)}
        >
          <option value="new">New</option>
          <option value="processing">Processing</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      ),
    },
  ];

  // Photo Orders Table Columns
  const photoOrderColumns = [
    {
      name: "Order No",
      selector: row => row.order_number,
      cell: row => (
        <span
          style={{ color: "#007bff", cursor: "pointer", fontWeight: 600 }}
          onClick={() => navigate(`/photo-order/${row.id}`)}
        >
          {row.order_number}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Title",
      selector: row => row.title,
      sortable: true,
    },
    {
      name: "Name",
      selector: row => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: row => row.email,
      sortable: true,
    },
    {
      name: "Status",
      selector: row => row.status,
      sortable: true,
    },
    {
      name: "Ordered Date",
      selector: row => row.ordered_date,
      sortable: true,
    },
  ];

  /* =======================
     EXPORT TO EXCEL
  ======================= */
  const exportToExcel = async () => {
    if (filteredOrders.length === 0) {
      NotificationManager.warning(langJson?.noordersfound || "No orders to export");
      return;
    }

    setExporting(true);
    try {
      const exportData = filteredOrders.map(order => ({
        // Order Information
        [langJson?.orderno || "Order No"]: order.order_number,
        [langJson?.orderdate || "Order Date"]: order.ordered_date,
        [langJson?.status || "Status"]: order.status,

        // Customer Information
        [langJson?.name || "Name"]: order.name,
        [langJson?.email || "Email"]: order.email,
        "Phone": order.phone || "",
        "Address": order.address || "",
        "City": order.city || "",
        "State": order.state || "",
        [langJson?.country || "Country"]: order.country,
        "Postal Code": order.shipping.zip_code || "",

        // Product Information
        //"Product Name": order.product_name || order.title || "",
        //"Product SKU": order.sku || order.article_no || "",
        [langJson?.quantity || "Qty"]: order.quantity,
        // "Unit Price": `${order.currency || "₹"}${order.price || 0}`,

        // Financial Information
        "Subtotal": `${order.currency || "₹"}${order.sub_total || 0}`,
        // "Tax %": order.tax || "0",
        "Tax": `${order.currency || "₹"}${order.tax_total || 0}`,
        [langJson?.price || "Total"]: `${order.currency || "₹"}${order.total_amount || 0}`,

        // Payment Information
        "Payment Method": order.payment_method
          ? order.payment_method
            .split("-")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
          : "",
        "Payment Status": order.payment_status?.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)) || "",
        "Currency": order.currency || "₹",

        // Shipping Information
        "Shipping Address": order.shipping_address || order.address || "",
        //"Shipping Method": order.shipping_method || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 15 }, // Order No
        { wch: 15 }, // Order Date
        { wch: 12 }, // Status
        { wch: 20 }, // Name
        { wch: 25 }, // Email
        { wch: 15 }, // Phone
        { wch: 25 }, // Address
        { wch: 15 }, // City
        { wch: 12 }, // State
        { wch: 15 }, // Country
        { wch: 12 }, // Postal Code
        //{ wch: 20 }, // Product Name
        //{ wch: 12 }, // Product SKU
        { wch: 8 },  // Qty
        //{ wch: 12 }, // Unit Price
        { wch: 12 }, // Subtotal
        //{ wch: 8 },  // Tax %
        { wch: 12 }, // Tax Amount
        { wch: 12 }, // Total
        { wch: 18 }, // Payment Method
        { wch: 15 }, // Payment Status
        { wch: 10 }, // Currency
        { wch: 25 }, // Shipping Address
        //{ wch: 15 }, // Shipping Method
      ];
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

      const fileName = `orders_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      NotificationManager.success(langJson?.exportedsuccessfully || "Exported successfully");
    } catch (err) {
      console.error("Export failed", err);
      NotificationManager.error(langJson?.exportfailed || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  /* =======================
     INIT
  ======================= */
  useEffect(() => {
    if (user) fetchOrders();
  }, [user]);

  /* =======================
     RENDER
  ======================= */
  return (
    <>
      <VendorHeader />
      <div className="adminDash">
        <Sidebar />
        <div className="rightSide rightTop">
          <h2>{langJson?.myorders || "My Orders"}</h2>
          <div className="prdtTopRight" style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <input
              className="dateInputFilter searchInput"
              type="text"
              placeholder={`${langJson?.search || "Search"} ${langJson?.orders || "Orders"}`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475168" }}>
                {langJson?.fromdate || "From Date"}
              </label>
              <input
                className="dateInputFilter"
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475168" }}>
                {langJson?.todate || "To Date"}
              </label>
              <input
                className="dateInputFilter"
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
            <button
              className="exportBtn"
              onClick={exportToExcel}
              disabled={exporting}
            >
              {exporting ? `${langJson?.exporting || "Exporting"}...` : langJson?.export || "Export"}
            </button>
          </div>
          <div className="prdtTable">
            <DataTable
              key="vendor-order-table"
              className="Tbl productListDataTable"
              columns={columns}
              data={filteredOrders}
              progressPending={loading}
              pagination
              highlightOnHover
              responsive
              noDataComponent={langJson?.noordersfound || "No orders found"}
            />
          </div>
          <h2 style={{ marginTop: 40 }}>Photo Orders</h2>
          <div className="prdtTopRight">
            <input
              type="text"
              placeholder="Search Photo Orders"
              value={searchPhotoQuery}
              onChange={e => setSearchPhotoQuery(e.target.value)}
            />
          </div>
          <div className="prdtTable">
            <DataTable
              key="photo-order-table"
              className="Tbl productListDataTable"
              columns={photoOrderColumns}
              data={filteredPhotoOrders}
              progressPending={loadingPhoto}
              pagination
              highlightOnHover
              responsive
              noDataComponent="No photo orders found"
            />
          </div>
        </div>
      </div>
      <VendorFooter />
    </>
  );
}

export default VendorOrderListing;
